import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Note } from '@/components/Note'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  PackContentSize,
  useUpdateWholesalerMarginsFmc,
  useWholesalerMarginsFmcList,
  wholesalerMarginsFmcRawExportQueryOptions,
} from '@/hooks/queries/crt/useWholesalerMarginsFmc'
import { useLastUpdatedWholesalerMarginsFmcLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { UpdateWholesalerMarginsFmc } from '@repo/backend/crt'
import { getFmcMarginPackContentSizes, MarkingCode } from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MarginsFmcDataTable } from '../../-shared/margins/MarginsFmcDataTable'
import { EditedMarkingValues, MarginsFmcRow } from '../../-shared/margins/types'
import { MarginsNavigationButtons } from '../../-shared/margins/MarginsNavigationButtons'
import { UploadMarginsDataDialog } from '../../-shared/margins/UploadMarginsDataDialog'

const PACK_CONTENT_SIZE_OPTIONS: readonly PackContentSize[] = [20, 25]

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/data-sources/wholesaler-margins/fmc/',
)({
  component: RouteComponent,
})

function getRowKey(row: MarginsFmcRow) {
  return `${row.packContentSize}|${row.brand}`
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'crt_wholesaler_margins_fmc_edit')
  const category = t('common.category.wholesaler', { ns: 'common' })

  const [packContentSize, setPackContentSize] = useState<PackContentSize>(
    PACK_CONTENT_SIZE_OPTIONS[0],
  )
  const [editedValues, setEditedValues] = useState<Map<string, EditedMarkingValues>>(new Map())

  const { data, isPending, error } = useWholesalerMarginsFmcList(packContentSize)
  const { data: lastUpdatedTrail } = useLastUpdatedWholesalerMarginsFmcLog()
  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateMargins, isPending: isSaving } = useUpdateWholesalerMarginsFmc()

  const uncoveredMaterialsCount = data?.uncoveredMaterialsCount ?? 0
  const rows = (data?.results ?? []) as MarginsFmcRow[]

  const handlePackContentSizeChange = (next: string) => {
    setPackContentSize(Number(next) as PackContentSize)
    setEditedValues(new Map())
  }

  const handleCommitValue = (row: MarginsFmcRow, markingKey: MarkingCode, value: string) => {
    const originalValue = row[markingKey] ?? ''
    const key = getRowKey(row)

    setEditedValues((prev) => {
      const next = new Map(prev)
      const existing = next.get(key) ?? {}
      const isUnchanged =
        value === originalValue ||
        (value !== '' && originalValue !== '' && Number(value) === Number(originalValue))

      if (isUnchanged) {
        const rest: EditedMarkingValues = { ...existing }
        delete rest[markingKey]
        if (Object.keys(rest).length === 0) {
          next.delete(key)
        } else {
          next.set(key, rest)
        }
        return next
      }

      next.set(key, { ...existing, [markingKey]: value })
      return next
    })
  }

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateWholesalerMarginsFmc['updates'] = []
    for (const [key, values] of editedValues) {
      const [packSize, brand] = key.split('|')
      if (!packSize || !brand) continue
      const normalized: Partial<Record<MarkingCode, string | null>> = {}
      for (const [k, v] of Object.entries(values) as [MarkingCode, string][]) {
        normalized[k] = v === '' ? null : v
      }
      updates.push({
        brand,
        packContentSize: Number(packSize) as PackContentSize,
        values: normalized,
      })
    }

    updateMargins(
      { updates },
      {
        onSuccess: () => setEditedValues(new Map()),
      },
    )
  }

  const handleBulkEdit = (
    selectedRows: MarginsFmcRow[],
    values: Partial<Record<MarkingCode, string>>,
  ) => {
    if (selectedRows.length === 0 || Object.keys(values).length === 0) return

    const updates: UpdateWholesalerMarginsFmc['updates'] = selectedRows.map((row) => ({
      brand: row.brand,
      packContentSize: row.packContentSize as PackContentSize,
      values,
    }))

    updateMargins(
      { updates },
      {
        onSuccess: () => {
          setEditedValues((prev) => {
            if (prev.size === 0) return prev
            const next = new Map(prev)
            for (const row of selectedRows) {
              next.delete(getRowKey(row))
            }
            return next.size === prev.size ? prev : next
          })
        },
      },
    )
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread>
        <PageHeader>{t('margins.fmc.pageHeader', { category })}</PageHeader>
        <HStack gap="sm">
          <MarginsNavigationButtons category="wholesaler" kind="fmc" />
          <PermissionsGuard required={['crt_wholesaler_margins_fmc_edit']}>
            <UploadMarginsDataDialog
              uploadType="import-wholesaler-margins-fmc"
              i18nKeyPrefix="importWholesalerMarginsFmcDialog"
            />
          </PermissionsGuard>
          <PermissionsGuard required={['crt_wholesaler_margins_fmc_view']}>
            <ExportCsvButton
              getCsvExport={() =>
                queryClient.ensureQueryData(wholesalerMarginsFmcRawExportQueryOptions())
              }
              rowCount={rows.length}
              canExceedRowCountLimit
            />
          </PermissionsGuard>
        </HStack>
      </Spread>

      {uncoveredMaterialsCount > 0 && (
        <Note
          tone="warning"
          icon={AlertTriangleIcon}
          note={t('margins.fmc.uncoveredMaterials.banner', { count: uncoveredMaterialsCount })}
          action={
            <PermissionsGuard required={['crt_materials_materials_list_view']}>
              <Button type="button" variant="secondary" size="sm" asChild>
                <Link to="/crt/materials/materials-list" search={{ isFmcPackSizeStandard: false }}>
                  <ExternalLinkIcon className="size-4" />
                  {t('margins.fmc.uncoveredMaterials.viewMaterials')}
                </Link>
              </Button>
            </PermissionsGuard>
          }
        />
      )}

      <VStack gap="md">
        <Select value={String(packContentSize)} onValueChange={handlePackContentSizeChange}>
          <SelectTrigger
            id="wholesaler-fmc-pack-content-size-select"
            className="bg-background w-72"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PACK_CONTENT_SIZE_OPTIONS.map((value) => (
              <SelectItem key={value} value={String(value)}>
                {t('margins.fmc.packSizeOption', {
                  sizes: getFmcMarginPackContentSizes(value).join(' & '),
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <MarginsFmcDataTable
          rows={rows}
          isPending={isPending}
          error={error}
          editedValues={editedValues}
          canEdit={canEdit}
          category={category}
          domIdPrefix="wholesaler-fmc"
          getRowKey={getRowKey}
          onCommitValue={handleCommitValue}
          onBulkEdit={handleBulkEdit}
        />
      </VStack>

      <PermissionsGuard required={['crt_wholesaler_margins_fmc_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
